import { useMutation, useQuery } from '@tanstack/react-query'
import type { DownloadedFile } from '@westhetics/api-client'
import { useApi } from '@/lib/api-context'

/**
 * A assinatura pelo link (docs/api/assinatura.md; docs/areas/documentos-e-assinaturas.md, "Assinatura
 * no celular do paciente"). Sem sessão: o token do link vai em `?t=` em toda chamada. A página nunca
 * chama `fetch`.
 */

export interface SigningDocument {
  id: string
  position: number
  /** "Termo de consentimento", "Contrato", "Anamnese". */
  type: string
  title: string
  summary: string | null
  risks: string | null
  markdown: string
  hash: string
  readAt: string | null
}

export interface Signing {
  state: 'open' | 'reading' | 'ready_to_sign' | 'signed'
  protocolNumber: string
  signer: { role: 'patient' | 'guardian'; firstName: string; name: string }
  patient: { firstName: string; name: string }
  clinic: { name: string }
  procedure: string | null
  estimatedMinutes: number
  linkExpiresAt: string
  anamnesis: { mode: string } | null
  documents: SigningDocument[]
  evidenceNotice: string
  acceptanceText: string
  copyTo: string | null
  signature: {
    signedAt: string
    signedAtLocalTime: string
    protocolNumber: string
    hash: string
    fullHash: string
    copySentTo: string | null
    isAwaitingClinicSigner: boolean
  } | null
  download: { receipt: string; documents: { id: string; pdf: string }[] } | null
}

export interface ReadResult {
  documentId: string
  readAt: string
  nextDocumentId: string | null
  hasReadAll: boolean
  state: 'reading' | 'ready_to_sign'
}

export interface SignInput {
  drawnSignature: { format: 'png'; content: string }
  accepted: true
  documentHashes: { documentId: string; hash: string }[]
  device?: {
    platform?: string
    browser?: string
    screenWidth?: number
    timezone?: string
    language?: string
  }
}

export interface Signed {
  state: 'signed'
  protocolNumber: string
  signedAt: string
  signedAtLocalTime: string
  hash: string
  fullHash: string
  device: string | null
  documents: { id: string; title: string; hash: string; pdf: string }[]
  copySentTo: string | null
  isClinicNotified: boolean
  isAwaitingClinicSigner: boolean
  download: { receipt: string }
}

export interface Declined {
  state: 'declined'
  declinedAt: string
  protocolNumber: string
}

export const signingKeys = {
  opening: (token: string) => ['signature', token] as const,
}

export function useSigning(token: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: signingKeys.opening(token ?? ''),
    queryFn: () => api.get<Signing>('/signature', { t: token }),
    enabled: Boolean(token),
    retry: false,
  })
}

const withToken = (path: string, token: string | null) =>
  `${path}?t=${encodeURIComponent(token ?? '')}`

export function useReadDocument(token: string | null) {
  const api = useApi()
  return useMutation({
    mutationFn: (input: { documentId: string; hash: string; scale: 'normal' | 'large' }) =>
      api.post<ReadResult>(withToken(`/signature/documents/${input.documentId}/read`, token), {
        hash: input.hash,
        scale: input.scale,
      }),
  })
}

export function useSign(token: string | null) {
  const api = useApi()
  return useMutation({
    mutationFn: (input: SignInput) => api.post<Signed>(withToken('/signature/sign', token), input),
  })
}

export function useDecline(token: string | null) {
  const api = useApi()
  return useMutation({
    mutationFn: (reason: string) =>
      api.post<Declined>(withToken('/signature/decline', token), { reason }),
  })
}

export function useReceipt(token: string | null) {
  const api = useApi()
  return useMutation({
    mutationFn: () => api.download('/signature/receipt', { t: token }),
  })
}

/** Entrega o arquivo baixado ao navegador com o nome que a API deu (RN-DOC-28). */
export function saveFile(file: DownloadedFile, fallbackName: string) {
  const url = URL.createObjectURL(file.blob)
  const a = document.createElement('a')
  a.href = url
  a.download = file.fileName ?? fallbackName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
